import React, { useCallback, useMemo, useState } from 'react';
import { useSellerGamificationOptional } from '../../../hooks/useSellerGamification';
import { AchievementIcon } from './gamification.ui';
import type { CelebrationItem } from '../../shared/celebration/celebration.types';
import { CelebrationHost } from '../../shared/celebration/CelebrationHost';
import {
  mergeCelebrations,
  peekCelebrationQueue,
  removeCelebrationFromQueue,
} from './celebrationQueue';

function toCelebrationItem(
  item: {
    id?: string;
    code: string;
    title: string;
    description: string;
    xp: number;
    category: string;
    href: string;
    unlockedAt?: string;
  },
): CelebrationItem {
  return {
    id: item.id ?? item.code,
    code: item.code,
    title: item.title,
    description: item.description,
    xp: item.xp,
    category: item.category,
    href: item.href,
    unlockedAt: item.unlockedAt,
  };
}

export const SellerCelebrationModal: React.FC = () => {
  const ctx = useSellerGamificationOptional();
  const [queueVersion, setQueueVersion] = useState(0);
  const [locallyDismissed, setLocallyDismissed] = useState<Set<string>>(() => new Set());
  const serverPending = ctx?.data?.pendingCelebrations ?? [];
  const celebrations = useMemo(
    () => mergeCelebrations(serverPending, peekCelebrationQueue()),
    [serverPending, queueVersion],
  );

  const items = useMemo(
    () =>
      celebrations
        .map(toCelebrationItem)
        .filter((item) => !locallyDismissed.has(item.id ?? item.code)),
    [celebrations, locallyDismissed],
  );

  const handleDismiss = useCallback(
    async (id: string) => {
      setLocallyDismissed((prev) => new Set(prev).add(id));
      removeCelebrationFromQueue(id);
      setQueueVersion((v) => v + 1);
      try {
        const serverItem = celebrations.find((c) => (c.id ?? c.code) === id);
        if (serverItem?.id && ctx?.dismissCelebration) {
          await ctx.dismissCelebration(serverItem.id);
        } else if (ctx?.reload) {
          await ctx.reload();
        }
      } catch {
        // UI already closed — server will reconcile on next sync
      }
    },
    [celebrations, ctx],
  );

  if (import.meta.env.VITE_E2E === '1') return null;

  return (
    <CelebrationHost
      persona="seller"
      items={items}
      lockedAchievements={ctx?.data?.lockedAchievements ?? null}
      onDismiss={handleDismiss}
      progressHref="/seller/progress"
      modalTestId="seller-celebration-modal"
      enabled={items.length > 0}
      renderIcon={(item) => (
        <AchievementIcon code={item.code} category={item.category ?? 'journey'} size="lg" />
      )}
    />
  );
};
